import React, { Component } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { Button, Input, Label, Icon, Form, Popup, List, Segment } from 'semantic-ui-react';

export const MAX_LOCATIONS = 10;
const LOOKUP_DELAY_MS = 400;
const SEARCH_DELAY_MS = 700;
const MIN_SEARCH_LENGTH = 3;
const MAX_RESULTS = 15;
// Picking waits for the post counts, but not for one slow lookup (they time out at 15s).
const COUNTS_WAIT_MS = 8000;

const BLUE = '#1e5b99';
const RED = '#db2828';
const searchAreaStyle = { position: 'relative', marginBottom: 10 };
const chipsStyle = { display: 'flex', flexWrap: 'wrap', alignItems: 'center' };
const chipStyle = { marginTop: 4, marginRight: 4, marginLeft: 0, border: 'none', background: '#e8f1fb', color: BLUE };
const chipLinkStyle = { color: BLUE, opacity: 1 };
const chipIconStyle = { color: BLUE };
const markerStyle = { color: RED };
const countStyle = { marginLeft: 'auto', paddingLeft: 12, color: 'rgba(0,0,0,.4)', fontSize: '0.92857143em' };
const headerStyle = { display: 'flex', alignItems: 'center', marginBottom: 4 };
const labelStyle = { fontSize: '0.92857143em', fontWeight: 700, color: 'rgba(0,0,0,.87)' };
const previewStyle = {
  display: 'flex',
  alignItems: 'center',
  margin: '0 0 12px',
  padding: '12px 14px',
  background: '#fff',
  border: '1px solid rgba(34,36,38,.12)',
  borderLeft: `3px solid ${BLUE}`,
  borderRadius: 4,
  boxShadow: '0 2px 6px rgba(34,36,38,.08)',
};
const previewBadgeStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  width: 40,
  height: 40,
  marginRight: 12,
  borderRadius: '50%',
  background: '#e8f1fb',
  color: BLUE,
};
const previewBadgeIconStyle = { margin: 0, color: RED };
const previewBodyStyle = { flex: 1, minWidth: 0 };
const previewNameStyle = { fontWeight: 700, fontSize: '1.07em', color: 'rgba(0,0,0,.87)' };
const previewOpenStyle = { marginLeft: 6, color: 'rgba(0,0,0,.4)' };
const previewDetailStyle = {
  display: 'flex',
  alignItems: 'center',
  marginTop: 4,
  color: 'rgba(0,0,0,.55)',
  fontSize: '0.92857143em',
};
const categoryStyle = {
  marginRight: 8,
  padding: '2px 8px',
  borderRadius: 10,
  background: '#f1f2f3',
  color: 'rgba(0,0,0,.65)',
  fontSize: '0.92857143em',
};
const previewActionsStyle = { display: 'flex', alignItems: 'center', flexShrink: 0, marginLeft: 12 };
// Floats over the content below, like a dropdown, so the settings box doesn't grow.
const resultsStyle = {
  position: 'absolute',
  top: '100%',
  left: 0,
  right: 0,
  zIndex: 20,
  margin: '4px 0 0',
  padding: '6px 4px',
  boxShadow: '0 4px 14px rgba(34,36,38,.2)',
};
const resultsListStyle = { maxHeight: 420, overflowY: 'auto', margin: 0 };
const resultsTitleStyle = { padding: '2px 10px 6px', color: 'rgba(0,0,0,.5)', fontSize: '0.92857143em' };
const resultIconStyle = { color: RED };
// Semantic doesn't dim disabled items in a selection list.
const waitingResultStyle = { opacity: 0.55, cursor: 'default' };
const resultAddedStyle = { color: 'rgba(0,0,0,.4)', fontSize: '0.92857143em' };
const resultPostsStyle = { color: 'rgba(0,0,0,.55)', fontSize: '0.92857143em', whiteSpace: 'nowrap' };
const cancelStyle = { background: 'transparent', boxShadow: 'none', color: 'rgba(0,0,0,.55)', fontWeight: 400 };

export const parseLocationId = value => {
  const text = _.trim(value);
  if (/^\d+$/.test(text)) return text;
  const match = text.match(/instagram\.com\/explore\/locations\/(\d+)/i);
  return match ? match[1] : null;
};

const normalise = text => _.trim(_.deburr(_.toLower(text)).replace(/[^a-z0-9]+/g, ' '));

// Keeps only the places whose name contains the text; when none do (e.g. a typo), keeps Instagram's own guesses.
export const filterSearchResults = (results, text) => {
  const query = normalise(text);
  const unique = _.uniqBy(results, 'id');
  const matching = _.filter(unique, result => _.includes(normalise(result.name), query));
  return _.isEmpty(matching) ? unique : matching;
};

// Most-tagged first: duplicates of a place differ hugely (12,756 posts vs 3), places without a count go last.
export const sortByPosts = (results, details) =>
  _.sortBy(results, result => {
    const count = _.get(details[result.id], 'mediaCount');
    return _.isNumber(count) ? -count : 1;
  });

export const locationUrl = ({ id, slug }) =>
  `https://www.instagram.com/explore/locations/${id}/${slug ? `${slug}/` : ''}`;

export const formatPosts = mediaCount =>
  (_.isNumber(mediaCount) ? `${mediaCount.toLocaleString('en-GB')} ${mediaCount === 1 ? 'post' : 'posts'}` : null);

const HelpPopup = () => <Popup
  content={`Instagram posts and stories tagged at any of these locations are added to the campaign.
    Search for a place by name or paste a location link from Instagram, check the preview, then add it.
    Up to ${MAX_LOCATIONS} locations.`}
  trigger={<Icon name="help circle" className="circular" />}
/>;

export default class InstagramLocationsInput extends Component {
  constructor(props) {
    super(props);
    this.state = {
      value: '',
      isLookingUp: false,
      message: null,
      preview: null,
      results: null,
      details: {},
      showResults: false,
      countsReady: false,
    };
    this.lookupRequest = 0;
    // Separate from lookupRequest so picking a result or cancelling doesn't stop the post counts loading.
    this.detailsRequest = 0;
    this.lookupLater = _.debounce(this.lookup, LOOKUP_DELAY_MS);
    this.searchLater = _.debounce(this.search, SEARCH_DELAY_MS);
  }

  componentDidMount() {
    document.addEventListener('mousedown', this.onDocumentMouseDown);
  }

  componentWillUnmount() {
    document.removeEventListener('mousedown', this.onDocumentMouseDown);
    clearTimeout(this.countsTimer);
    this.cancelPending();
    this.detailsRequest += 1;
  }

  // Closes the results list on a click anywhere outside the input and the list.
  onDocumentMouseDown = e => {
    if (this.state.showResults && this.searchArea && !this.searchArea.contains(e.target)) {
      this.setState({ showResults: false });
    }
  }

  onInputFocus = () => {
    if (this.state.results && !this.state.preview) this.setState({ showResults: true });
  }

  onKeyDown = e => {
    if (e.key === 'Escape' && this.state.showResults) this.setState({ showResults: false });
  }

  // Looks a pasted link or id up straight away, and searches by name once typing pauses.
  onInputChange = (e, { value }) => {
    this.cancelPending();
    this.detailsRequest += 1;
    this.setState({ value, message: null, preview: null, results: null, details: {}, isLookingUp: false });
    if (parseLocationId(value)) {
      this.lookupLater(value, false);
    } else if (this.canSearch(value)) {
      this.searchLater(value, false);
    }
  }

  onKeyPress = e => {
    if (e.key !== 'Enter') return;
    // Stops Enter from submitting a surrounding form.
    e.preventDefault();
    if (this.state.preview) {
      this.onConfirm();
    } else {
      this.onFind();
    }
  }

  onFind = e => {
    if (e) e.preventDefault();
    const { value } = this.state;
    this.cancelPending();
    if (parseLocationId(value) || !this.props.onSearch) {
      this.lookup(value, true);
    } else {
      this.search(value, true);
    }
  }

  // Uses the post count lookup when it has arrived, otherwise looks the place up now.
  onPick = result => {
    if (!this.state.countsReady || _.some(this.props.locations, { id: result.id }) || this.isFull()) return;
    const detail = this.state.details[result.id];
    if (detail) {
      this.cancelPending();
      this.setState({ preview: detail, message: null, isLookingUp: false });
      return;
    }
    this.lookup(result.id, true);
  }

  onConfirm = e => {
    if (e) e.preventDefault();
    const { locations, isSaving, onChange } = this.props;
    const { preview } = this.state;
    if (!preview || isSaving || this.isFull()) return;
    if (_.some(locations, { id: preview.id })) {
      this.setState({ preview: null, message: 'That location has already been added.' });
      return;
    }
    this.detailsRequest += 1;
    this.setState({ value: '', preview: null, message: null, results: null, details: {} });
    onChange([...locations, _.pick(preview, ['id', 'name', 'slug'])]);
  }

  // Goes back to the search results when the preview came from one.
  onCancel = e => {
    if (e) e.preventDefault();
    this.cancelPending();
    if (this.state.results) {
      this.setState({ preview: null, message: null, isLookingUp: false, showResults: true });
    } else {
      this.setState({ value: '', preview: null, message: null, isLookingUp: false });
    }
  }

  onDelete = id => {
    const { locations, isSaving, onChange } = this.props;
    if (isSaving) return;
    this.setState({ message: null });
    onChange(_.reject(locations, { id }));
  }

  // explicit: the user asked for it, so explain why nothing was looked up.
  lookup = (value, explicit) => {
    const { locations, onLookup } = this.props;
    if (!_.trim(value) || this.isFull()) return null;

    const id = parseLocationId(value);
    if (!id) {
      if (explicit) this.setState({ message: 'Paste a link like instagram.com/explore/locations/111106416225578/…' });
      return null;
    }
    if (_.some(locations, { id })) {
      this.setState({ message: 'That location has already been added.' });
      return null;
    }

    this.lookupRequest += 1;
    const request = this.lookupRequest;
    const isCurrent = () => request === this.lookupRequest;
    this.setState({ isLookingUp: true, message: null, preview: null });
    return onLookup(id)
      .then(location => {
        if (!isCurrent()) return;
        if (!location) {
          this.setState({ isLookingUp: false, message: 'We couldn’t find that location on Instagram.' });
          return;
        }
        this.setState({ isLookingUp: false, preview: location });
      })
      .catch(() => {
        if (!isCurrent()) return;
        this.setState({ isLookingUp: false, message: 'Could not look up the location. Please try again.' });
      });
  }

  search = (value, explicit) => {
    const { onSearch } = this.props;
    const text = _.trim(value);
    if (!text || this.isFull()) return null;
    if (!this.canSearch(text)) {
      if (explicit) this.setState({ message: `Type at least ${MIN_SEARCH_LENGTH} letters to search.` });
      return null;
    }

    this.lookupRequest += 1;
    this.detailsRequest += 1;
    const request = this.lookupRequest;
    const isCurrent = () => request === this.lookupRequest;
    this.setState({ isLookingUp: true, message: null, preview: null, results: null, details: {}, countsReady: false });
    return onSearch(text)
      .then(found => {
        if (!isCurrent()) return null;
        const results = _.take(filterSearchResults(found, text), MAX_RESULTS);
        if (_.isEmpty(results)) {
          this.setState({ isLookingUp: false, message: `No Instagram locations match “${text}”.` });
          return null;
        }
        this.setState({ isLookingUp: false, results, showResults: true });
        return this.loadDetails(results);
      })
      .catch(() => {
        if (!isCurrent()) return;
        this.setState({ isLookingUp: false, message: 'Could not search locations. Please try again.' });
      });
  }

  // Looks every result up in parallel for its post count, then sorts once so rows don't jump while loading.
  // A failed or unknown lookup is stored as null and just shows no count.
  loadDetails = results => {
    const request = this.detailsRequest;
    const isCurrent = () => request === this.detailsRequest;
    const allLoaded = Promise.all(_.map(results, result => this.props.onLookup(result.id)
      .catch(_.constant(null))
      .then(location => {
        if (!isCurrent()) return;
        this.setState(({ details }) => ({ details: { ...details, [result.id]: location } }));
      })));
    const waited = new Promise(resolve => { this.countsTimer = setTimeout(resolve, COUNTS_WAIT_MS); });
    return Promise.race([allLoaded, waited])
      .then(() => {
        clearTimeout(this.countsTimer);
        if (!isCurrent()) return;
        this.setState(state => ({ results: sortByPosts(state.results, state.details), countsReady: true }));
      });
  }

  cancelPending = () => {
    this.lookupLater.cancel();
    this.searchLater.cancel();
    this.lookupRequest += 1;
  }

  canSearch = value => !!this.props.onSearch && _.size(_.trim(value)) >= MIN_SEARCH_LENGTH;

  isFull = () => _.size(this.props.locations) >= MAX_LOCATIONS;

  renderResults = results => (
    <Segment style={resultsStyle} className="location-results">
      { !this.state.countsReady && <div style={resultsTitleStyle}>Loading post counts…</div> }
      <List selection divided verticalAlign="middle" style={resultsListStyle}>
        {
          _.map(results, result => {
            const isAdded = _.some(this.props.locations, { id: result.id });
            return (
              <List.Item
                key={result.id}
                onClick={() => this.onPick(result)}
                disabled={isAdded || !this.state.countsReady}
                style={this.state.countsReady ? undefined : waitingResultStyle}
              >
                <List.Content floated="right">{this.renderResultStatus(result, isAdded)}</List.Content>
                <List.Icon name="marker" style={resultIconStyle} />
                <List.Content>
                  <List.Header>{result.name}</List.Header>
                  {
                    (result.address || result.city) &&
                    <List.Description>{_.join(_.compact([result.address, result.city]), ', ')}</List.Description>
                  }
                </List.Content>
              </List.Item>
            );
          })
        }
      </List>
    </Segment>
  );

  renderResultStatus = (result, isAdded) => {
    if (isAdded) return <span style={resultAddedStyle}>Added</span>;
    const { details } = this.state;
    if (!_.has(details, result.id)) return <Icon name="spinner" loading color="grey" />;
    return <span style={resultPostsStyle}>{formatPosts(_.get(details[result.id], 'mediaCount'))}</span>;
  }

  renderPreview = preview => (
    <div style={previewStyle} className="location-preview">
      <div style={previewBadgeStyle}>
        <Icon name="marker" size="large" style={previewBadgeIconStyle} />
      </div>
      <div style={previewBodyStyle}>
        <div style={previewNameStyle}>
          {preview.name}
          <a
            href={locationUrl(preview)}
            target="_blank"
            rel="noopener noreferrer"
            title="Open on Instagram"
            style={previewOpenStyle}
          >
            <Icon name="external" />
          </a>
        </div>
        <div style={previewDetailStyle}>
          {preview.category && <span style={categoryStyle}>{preview.category}</span>}
          {formatPosts(preview.mediaCount)}
        </div>
      </div>
      <div style={previewActionsStyle}>
        <Button type="button" size="small" content="Cancel" style={cancelStyle} onClick={this.onCancel} />
        <Button
          type="button"
          primary
          size="small"
          icon="plus"
          content="Add"
          loading={this.props.isSaving}
          disabled={this.props.isSaving}
          onClick={this.onConfirm}
        />
      </div>
    </div>
  );

  renderChip = location => (
    <Label key={location.id} size="large" style={chipStyle}>
      <Icon name="marker" style={markerStyle} />
      <a href={locationUrl(location)} target="_blank" rel="noopener noreferrer" style={chipLinkStyle}>
        {location.name}
      </a>
      <Icon name="close" link style={chipIconStyle} onClick={() => this.onDelete(location.id)} />
    </Label>
  );

  placeholder = () => (this.props.onSearch
    ? 'Search for a place, or paste an Instagram location link'
    : 'Paste an Instagram location link, e.g. instagram.com/explore/locations/…')

  render() {
    const { locations, error, hideLabel } = this.props;
    const { value, isLookingUp, message, preview, results, showResults } = this.state;
    const isFull = this.isFull();
    const shownMessage = error || message;

    return (
      <Form.Field>
        {
          !hideLabel &&
          <div style={headerStyle}>
            <label htmlFor="campaign-locations" style={labelStyle}>
              Tracked locations <HelpPopup />
            </label>
          </div>
        }
        {
          shownMessage ? (
            <Label basic color="red" pointing="below">
              {shownMessage}
            </Label>) : null
        }
        <div style={searchAreaStyle} ref={el => { this.searchArea = el; }}>
          <Input
            id="campaign-locations"
            fluid
            icon="instagram"
            iconPosition="left"
            loading={isLookingUp}
            disabled={isFull}
            onChange={this.onInputChange}
            onKeyPress={this.onKeyPress}
            onKeyDown={this.onKeyDown}
            onFocus={this.onInputFocus}
            placeholder={isFull
              ? `You can add up to ${MAX_LOCATIONS} locations`
              : this.placeholder()}
            value={value}
          />
          { !preview && showResults && !_.isEmpty(results) && this.renderResults(results) }
        </div>
        { preview && this.renderPreview(preview) }
        {
          !_.isEmpty(locations) &&
          <div style={chipsStyle}>
            { _.map(locations, this.renderChip) }
            <span style={countStyle}>{_.size(locations)} of {MAX_LOCATIONS}</span>
          </div>
        }
      </Form.Field>
    );
  }
}

InstagramLocationsInput.propTypes = {
  locations: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string,
    slug: PropTypes.string,
  })),
  onChange: PropTypes.func.isRequired,
  onLookup: PropTypes.func.isRequired,
  onSearch: PropTypes.func,
  isSaving: PropTypes.bool,
  error: PropTypes.string,
  hideLabel: PropTypes.bool,
};

InstagramLocationsInput.defaultProps = {
  locations: [],
  onSearch: null,
  isSaving: false,
  error: null,
  hideLabel: false,
};



/** WEBPACK FOOTER **
 ** ./src/components/instagram-locations-input/index.js
 **/